import React, { useEffect, useState } from "react";
import "../Messages/messages.css";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";

const JobCategories = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newArLabel, setNewArLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get("/job-categories/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load job categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchCategories();
  }, [token]);

  const updateLocalField = (id, field, value) => {
    setCategories((prev) =>
      prev.map((cat) => (cat._id === id ? { ...cat, [field]: value } : cat))
    );
  };

  const handleSave = async (cat) => {
    try {
      setSavingId(cat._id);
      const { data } = await axios.put(
        `/job-categories/${cat._id}`,
        {
          label: cat.label,
          arLabel: cat.arLabel,
          isActive: cat.isActive,
          sortOrder: cat.sortOrder,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories((prev) => prev.map((c) => (c._id === cat._id ? data : c)));
      toast.success("Category saved.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save category.");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"?`)) return;
    try {
      await axios.delete(`/job-categories/${cat._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories((prev) => prev.filter((c) => c._id !== cat._id));
      toast.success("Category deleted.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not delete category.");
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newLabel.trim() || !newArLabel.trim()) {
      toast.error("Both English and Arabic labels are required.");
      return;
    }
    try {
      setAdding(true);
      const { data } = await axios.post(
        "/job-categories",
        { label: newLabel.trim(), arLabel: newArLabel.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories((prev) => [...prev, data]);
      setNewLabel("");
      setNewArLabel("");
      toast.success("Category added.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add category.");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <BeatLoader />
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ margin: "0 0 16px" }}>Job Categories {`(${categories.length})`}</h1>

      <form
        onSubmit={handleAdd}
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          margin: "0 0 18px",
          padding: "12px 14px",
          background: "#f8f6f4",
          borderRadius: 12,
          border: "1px solid rgba(154, 116, 98, 0.18)",
        }}
      >
        <input
          type="text"
          placeholder="Label (English)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", minWidth: 200 }}
        />
        <input
          type="text"
          placeholder="Label (Arabic)"
          dir="rtl"
          value={newArLabel}
          onChange={(e) => setNewArLabel(e.target.value)}
          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", minWidth: 200 }}
        />
        <button type="submit" className="dash-btn dash-btn-primary" disabled={adding}>
          {adding ? "Adding..." : "Add category"}
        </button>
      </form>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {categories
          .slice()
          .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
          .map((cat) => (
            <div
              key={cat._id}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
                padding: "12px 14px",
                background: "#fff",
                borderRadius: 12,
                border: "1px solid rgba(154, 116, 98, 0.18)",
              }}
            >
              <input
                type="text"
                value={cat.label}
                onChange={(e) => updateLocalField(cat._id, "label", e.target.value)}
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", minWidth: 180 }}
              />
              <input
                type="text"
                dir="rtl"
                value={cat.arLabel}
                onChange={(e) => updateLocalField(cat._id, "arLabel", e.target.value)}
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", minWidth: 180 }}
              />
              <input
                type="number"
                value={cat.sortOrder ?? 0}
                onChange={(e) => updateLocalField(cat._id, "sortOrder", Number(e.target.value))}
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", width: 80 }}
                title="Sort order"
              />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", color: "#6b5344", fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={cat.isActive !== false}
                  onChange={(e) => updateLocalField(cat._id, "isActive", e.target.checked)}
                />
                Active
              </label>
              <button
                type="button"
                className="dash-btn"
                onClick={() => handleSave(cat)}
                disabled={savingId === cat._id}
              >
                {savingId === cat._id ? "Saving..." : "Save"}
              </button>
              <button type="button" className="dash-btn dash-btn-danger" onClick={() => handleDelete(cat)}>
                Delete
              </button>
            </div>
          ))}
      </div>
    </div>
  );
};

export default JobCategories;
